import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/rootReducer';
import StyledButton from '../../Common/StyledButton';
import StyledDeleteButton from '../../Common/StyledDeleteButton';
import DataRequestSuccessModal from './DataRequestSuccessModal';
import CustomModal from '../../Common/CustomModal';
import {
  closeDataRequestSuccessModal,
  coachDataRequest,
  resetCoachDeleteRequestSuccess,
} from '../../../redux/coach/coachSlice';
import AccountDeleteModal from './AccountDeleteModal';
import createPermissionCheckers from '../../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../../types/commonTypes';
import { logout } from '../../../redux/GlobalAction';
import { COACH_DELETE_REQUEST_VALIDATION } from '../../../service/graphql/coachQuery';
import { toastStyles } from '../../../utils/utils';

const StyledBox = styled(Box)(() => ({
  padding: '24px',
  boxShadow: ' 0px 4px 40px 0px #FFDDC840',
  backgroundColor: '#FFF',
  borderRadius: '10px',
  bgcolor: '#fff',
  margin: '15px',
}));

export default function PrivacySettings() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const coach = useAppSelector((state: RootState) => state.coach.coach);
  const {
    coachDataRequestSuccess,
    coachDataRequestLoading,
    coachDeleteRequestSuccess,
    coachDeleteRequestLoading,
  } = useAppSelector((state: RootState) => state.coach);
  const [isDataRequestSuccessModalOpen, setIsDataRequestSuccessModalOpen] = useState(false);
  const [isAccountDeleteModalOpen, setIsAccountDeleteModalOpen] = useState(false);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  const [fetchValidation, { loading }] = useLazyQuery(COACH_DELETE_REQUEST_VALIDATION, {
    fetchPolicy: 'network-only',
    onCompleted(data) {
      if (data.deleteRequestValidation) {
        setIsAccountDeleteModalOpen(true);
      } else {
        toast.error(
          t('settings.privacySettings.deleteRequest.deleteRequestValidation'),
          toastStyles.error
        );
      }
    },
  });

  const handleCoachDataRequest = () => {
    dispatch(coachDataRequest({ coachId: coach.id }));
  };

  const handleAccountDelete = () => {
    fetchValidation();
  };

  const handleDismiss = () => {
    setIsDataRequestSuccessModalOpen(false);
    dispatch(closeDataRequestSuccessModal());
  };

  useEffect(() => {
    if (coachDataRequestSuccess) {
      setIsDataRequestSuccessModalOpen(true);
    }
  }, [coachDataRequestSuccess]);

  useEffect(() => {
    if (coachDeleteRequestSuccess) {
      setIsAccountDeleteModalOpen(false);
      dispatch(logout());
      dispatch(resetCoachDeleteRequestSuccess());
    }
  }, [coachDeleteRequestSuccess]);

  return (
    <Grid container flexDirection="row" display="flex">
      <StyledBox width={400} maxHeight={470} p={4}>
        <Typography variant="bodyBold" color="primary.main">
          {t('settings.privacySettings.deleteRequest.title')}
        </Typography>
        <Grid container spacing={4.5} paddingTop="4px">
          <Grid item xs={12}>
            <Typography variant="body1" color="primary.main">
              {t('settings.privacySettings.deleteRequest.description')}
            </Typography>
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="start">
            <StyledDeleteButton
              loading={loading}
              loadingText={t('common.Processing')}
              onClick={handleAccountDelete}
              fullWidth={false}
              disabled={!checkPermissions(UserPermissionType.CORE_MODULE_DELETE_COACH_ACCOUNT)}
            >
              {t('settings.privacySettings.deleteRequest.deleteAccount')}
            </StyledDeleteButton>
          </Grid>
        </Grid>
      </StyledBox>

      <StyledBox width={400} maxHeight={470} p={4}>
        <Typography variant="bodyBold" color="primary.main">
          {t('settings.privacySettings.dataRequest.title')}
        </Typography>
        <Grid container spacing={4.5} paddingTop="4px">
          <Grid item xs={12}>
            <Typography variant="body1" color="primary.main">
              {t('settings.privacySettings.dataRequest.description')}
            </Typography>
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="start">
            <StyledButton
              loading={coachDataRequestLoading}
              loadingText={t('common.saving')}
              onClick={handleCoachDataRequest}
              fullWidth={false}
              disabled={!checkPermissions(UserPermissionType.CORE_MODULE_COACH_DATA_REQUEST)}
            >
              {t('settings.privacySettings.dataRequest.requestData')}
            </StyledButton>
          </Grid>
        </Grid>
      </StyledBox>
      {isDataRequestSuccessModalOpen && (
        <CustomModal isOpen={isDataRequestSuccessModalOpen} xs={3.5}>
          <DataRequestSuccessModal handleDismiss={handleDismiss} />
        </CustomModal>
      )}
      {isAccountDeleteModalOpen && (
        <CustomModal isOpen={isAccountDeleteModalOpen} xs={6} md={5} lg={3.5}>
          <AccountDeleteModal
            handleDismiss={() => setIsAccountDeleteModalOpen(false)}
            loading={coachDeleteRequestLoading}
          />
        </CustomModal>
      )}
    </Grid>
  );
}
